/* eslint-disable no-unused-vars */

import { IButtonDesignSettings } from '@/constants/chatbot'
import { BackendApiFetchDataPartialResponse, backendApiFetch } from '@/hooks/backend-api-fetch'
import { clientApiAxiosFetch } from '@/hooks/client-api-fetch'
import { fetchBackendApi } from '@/hooks/fetch-backend-api'
import { audioUploadFileName, toBareMimeType } from '@/lib/audio-recording'
import { ChatbotContactFormSchemaType } from '@/schema/chatbot-contact-schema'
import { ILimitedProps, IPublicRealtimeVoiceChatLimitStatus } from '@/stores/limit-usage.store'
import { EscalationVariablesType } from '@/types/escalation-chatbot'

import {
  CHATBOT_LOGO_SUPPORTED_IMAGE_TYPES,
  type ChatBannerSettingsSchema,
  type PublicChatBannerSchema,
  WELCOME_MESSAGE_IMAGE_DATA_SIZE_LIMIT,
  WELCOME_MESSAGE_IMAGE_SUPPORTED_IMAGE_TYPES,
} from '../schema/chatbot-schema'

import { getAuthSession } from './auth-session-service'
import { SubscriptionStatus } from './subscription-service'

import type { z } from 'zod'

// @NOTE: might be deprecated since we changed the behavior for multi domain training
export interface dashboardWebscrapeInterface {
  lastTrainedWebsiteId?: string
  lastTrainedWebsite?: string
}

export type BookingProviderValue = 'google_calendar' | 'microsoft_bookings' | 'microsoft_book_with_me' | 'other'

export interface ChatbotPayload extends BackendApiFetchDataPartialResponse, dashboardWebscrapeInterface {
  _id: string
  name: string
  lastTrainedFile: string
  websiteUrl: string
  welcomeMessage: string
  welcomeMessageImageUrl?: string
  welcomeMessageImageLink?: string
  themeColor: string
  /** Missing on older backend responses; consumers default to true. */
  showTextSizeButton?: boolean
  contactPageUrl: string
  // purpose: string
  bubbleLabel?: string
  logoUrl?: string
  maxFileUploadLimit?: number
  maxWebpagesPerMonth?: number
  header?: string
  connectedResponse?: string
  questionInputPrompt?: string
  enabledEscalation?: boolean
  planConfig?: {
    canSendEscalationDetail?: boolean
    canUseMicrophone?: boolean
    canUseVoiceChat?: boolean
    welcomeQuestions?: number
    canUseCollectContactInfo?: boolean
    canUseConversationStyle?: boolean
    canRemoveBranding?: boolean
    canExportConversations?: boolean
  }
  contactEscalationEmail: string
  contactPhoneNumber: string
  realtimeVoiceLanguage?: string
  isCollectContactInfo?: boolean
  isPasswordProtected?: boolean
  collectionContactPrompt?: string
  buttonDesignSettings?: IButtonDesignSettings
  escalationMessage?: string
  bookingSchedulingUrl?: string | null
  bookingProvider?: BookingProviderValue | null
  chatBanner?: z.input<typeof ChatBannerSettingsSchema> | null
  bookingTriggerHash?: string | null
}

export interface ChatbotLLMConfiguration extends BackendApiFetchDataPartialResponse {
  promptResponseStyle: string
  customPromptResponse?: string
}

interface UpdateChatbotParams {
  chatBanner?: Partial<Omit<z.infer<typeof ChatBannerSettingsSchema>, 'imageUrl' | 'ratios'>> & {
    ratios?: Partial<z.infer<typeof ChatBannerSettingsSchema>['ratios']>
  }
  name?: string
  welcomeMessage?: string
  welcomeMessageImageLink?: string
  themeColor?: string
  showTextSizeButton?: boolean
  contactPageUrl?: string
  // purpose: string
  bubbleLabel?: string
  header?: string
  connectedResponse?: string
  questionInputPrompt?: string
  enabledEscalation?: boolean
  contactEscalationEmail?: string
  contactPhoneNumber?: string
  realtimeVoiceLanguage?: string
  isCollectContactInfo?: boolean
  collectionContactPrompt?: string
  buttonDesignSettings?: IButtonDesignSettings
  escalationMessage?: string
  provideContactInfo?: boolean
}

interface PublicUpdateChatbotParams {
  name: string
  welcomeMessage: string
  themeColor: string
  logoUrl?: string
}

export const updateChatbotService = async (body: UpdateChatbotParams) => {
  // Image persistence has its own endpoint, including for untyped callers.
  if (body.chatBanner) {
    const { imageUrl: _imageUrl, ...chatBanner } = body.chatBanner as typeof body.chatBanner & { imageUrl?: unknown }
    body = { ...body, chatBanner }
  }
  const { data, ok } = await backendApiFetch({
    endpoint: 'chatbots',
    method: 'PUT',
    isProtected: true,
    body,
  })

  return {
    data,
    ok,
  }
}

interface UpdateChatbotLlmConfigurationParams {
  promptResponseStyle: string
  customPromptResponse?: string
}

export const updateChatbotLlmConfiguration = async (body: UpdateChatbotLlmConfigurationParams) => {
  const { data, ok } = await backendApiFetch({
    endpoint: 'chatbots/llm-configuration',
    method: 'PATCH',
    isProtected: true,
    body,
  })

  return {
    data,
    ok,
  }
}

export const getChatbotLLMConfiguration = async () => {
  const { data, ok } = await backendApiFetch<ChatbotLLMConfiguration>({
    endpoint: 'chatbots/llm-configuration',
    method: 'GET',
    isProtected: true,
  })

  return {
    data,
    ok,
  }
}

export const updateEscalationStatus = async (body: { enabledEscalation: boolean }) => {
  const { data, ok } = await backendApiFetch({
    endpoint: 'chatbots/update-escalation-status',
    method: 'PUT',
    isProtected: true,
    body,
  })
  return {
    data,
    ok,
  }
}

export const updateQuestionInputPrompt = async (body: { questionInputPrompt: string }) => {
  const { data, ok } = await backendApiFetch({
    endpoint: 'chatbots/update-question-input-prompt',
    method: 'PUT',
    isProtected: true,
    body,
  })
  return {
    data,
    ok,
  }
}

export const updateContactPageUrl = async (body: { contactPageUrl: string }) => {
  const { data, ok } = await backendApiFetch({
    endpoint: 'chatbots/update-contact-page-url',
    method: 'PUT',
    isProtected: true,
    body,
  })
  return {
    data,
    ok,
  }
}

export const updateBookingSchedulingUrl = async (body: { bookingSchedulingUrl: string; bookingTriggerHash: string }) => {
  const { data, ok } = await backendApiFetch({
    endpoint: 'chatbots/update-booking-scheduling-url',
    method: 'PUT',
    isProtected: true,
    body,
  })
  return {
    data,
    ok,
  }
}

export const getEscalationStatus = async () => {
  const { data, ok } = await backendApiFetch<{ enabledEscalation: boolean }>({
    endpoint: 'chatbots/escalation-status',
    method: 'GET',
    isProtected: true,
  })
  return {
    data,
    ok,
  }
}

export const getCurrentChatbotConfigService = async () => {
  const { data, ok } = await backendApiFetch<ChatbotPayload>({
    endpoint: 'chatbots',
    method: 'GET',
    isProtected: true,
  })

  return {
    data,
    ok,
  }
}

export const getChatbotContactInfoService = async () => {
  const { data, ok } = await backendApiFetch<{
    contactEscalationEmail: string
    contactPhoneNumber: string
    contactPageUrl: string
    provideContactInfo: boolean
  }>({
    endpoint: 'chatbots/chatbot-contact-info',
    method: 'GET',
    isProtected: true,
  })

  return {
    data,
    ok,
  }
}

// Resolved widget contract, rather than the dashboard's editable banner settings.
export type PublicChatBanner = NonNullable<z.infer<typeof PublicChatBannerSchema>>

export interface ChatbotPublicPayload {
  _id: string
  name: string
  welcomeMessage: string
  themeColor: string
  /** Missing on older backend responses; consumers default to true. */
  showTextSizeButton?: boolean
  purpose: string
  logoUrl?: string
  subscriptionStatus: SubscriptionStatus
  header: string
  connectedResponse: string
  questionInputPrompt: string
  enabledEscalation?: boolean
  welcomeMessageImageUrl?: string
  welcomeMessageImageLink?: string
  planConfig?: {
    canSendEscalationDetail?: boolean
    canUseMicrophone?: boolean
    canUseVoiceChat?: boolean
    welcomeQuestions?: number
    canUseCollectContactInfo?: boolean
    canRemoveBranding?: boolean
  }
  realtimeVoiceLanguage?: string
  isCollectContactInfo?: boolean
  limitReached?: boolean
  isPasswordProtected?: boolean
  collectionContactPrompt?: string
  escalationMessage?: string
  contactEscalationEmail?: string
  contactPhoneNumber?: string
  contactPageUrl?: string
  bookingSchedulingUrl?: string | null
  bookingProvider?: BookingProviderValue | null
  bookingTriggerHash?: string | null
  buttonDesignSettings?: IButtonDesignSettings
  chatBanner?: PublicChatBanner | null
}
export const getCurrentPublicChatbotConfigService = async (chatbotId: string) => {
  const { data, ok } = await backendApiFetch<ChatbotPublicPayload>({
    endpoint: 'chatbots/public/' + chatbotId,
    method: 'GET',
  })

  return {
    data,
    ok,
  }
}

interface UploadChatbotImageParams {
  file: File
}

export const uploadChatbotLogo = async ({ file }: UploadChatbotImageParams) => {
  if (!file) {
    throw new Error('File is required')
  }
  const contentType = file.type

  // Validate file type
  if (!CHATBOT_LOGO_SUPPORTED_IMAGE_TYPES.includes(contentType)) {
    throw new Error('File is not of supported image type')
  }

  const formData = new FormData()
  formData.append('file', file)

  // Construct the URL
  const apiUrl = process.env.NEXT_PUBLIC_API_URL
  const fullUrl = `${apiUrl}/v1/chatbots/logo`
  const session = await getAuthSession()

  const config = {
    method: 'POST',
    url: fullUrl,
    data: formData,
    headers: {
      'content-type': 'multipart/form-data',
      Authorization: `Bearer ${session?.jwtToken}`,
    },
  }
  try {
    const response = await clientApiAxiosFetch({ fullUrl, config })
    return response
  } catch (error) {
    console.error('Error uploading chatbot logo:', error)
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Error uploading chatbot logo')
  }
}

/** Banner images are persisted immediately, independently of the settings form. */
export const uploadChatbotBannerImage = async ({
  chatbotId,
  file,
}: UploadChatbotImageParams & { chatbotId: string }) => {
  if (!/^[a-f\d]{24}$/i.test(chatbotId)) throw new Error('Invalid chatbot id')
  if (!WELCOME_MESSAGE_IMAGE_SUPPORTED_IMAGE_TYPES.includes(file.type)) throw new Error('type')
  if (file.size > WELCOME_MESSAGE_IMAGE_DATA_SIZE_LIMIT * 1024 * 1024) throw new Error('size')
  const formData = new FormData()
  formData.append('image', file)
  const fullUrl = `${process.env.NEXT_PUBLIC_API_URL}/v1/chatbot/${chatbotId}/banner-image`
  const session = await getAuthSession()
  return clientApiAxiosFetch<{ data?: { imageUrl: string } }>({
    fullUrl,
    config: {
      method: 'POST',
      url: fullUrl,
      data: formData,
      headers: { 'content-type': 'multipart/form-data', Authorization: `Bearer ${session?.jwtToken}` },
    },
  })
}

export const removeChatbotBannerImage = async (chatbotId: string) => {
  if (!/^[a-f\d]{24}$/i.test(chatbotId)) throw new Error('Invalid chatbot id')
  return fetchBackendApi({ endpoint: `chatbot/${chatbotId}/banner-image`, method: 'DELETE', isProtected: true })
}

export const uploadChatbotWelcomeMessageImage = async ({ file }: UploadChatbotImageParams) => {
  if (!file) {
    throw new Error('File is required')
  }
  const contentType = file.type

  // Validate file type
  if (!WELCOME_MESSAGE_IMAGE_SUPPORTED_IMAGE_TYPES.includes(contentType)) {
    throw new Error('File is not of supported image type')
  }

  const formData = new FormData()
  formData.append('file', file)

  // Construct the URL
  const fullUrl = `${process.env.NEXT_PUBLIC_API_URL}/v1/chatbots/welcome-message-image`
  const session = await getAuthSession()

  const config = {
    method: 'POST',
    url: fullUrl,
    data: formData,
    headers: {
      'content-type': 'multipart/form-data',
      Authorization: `Bearer ${session?.jwtToken}`,
    },
  }
  try {
    const response = await clientApiAxiosFetch({ fullUrl, config })
    return response
  } catch (error) {
    console.error('Error uploading welcome message image:', error)
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Failed to upload welcome message image')
  }
}

export const removeChatbotWelcomeMessageImage = async () => {
  const { data, ok } = await fetchBackendApi({
    endpoint: 'chatbots/welcome-message-image',
    method: 'DELETE',
    isProtected: true,
  })

  return {
    data,
    ok,
  }
}

export const handleOverConversationToBusiness = async (
  chatSessionId: string,
  body: ChatbotContactFormSchemaType,
  locale?: string,
) => {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL

  const fullUrl = `${apiUrl}/v1/history/escalate/chat-sessions/${chatSessionId}`

  const formData = new FormData()
  Object.entries(body).forEach(([key, value]) => {
    if (key === 'files') {
      ;(value as File[]).forEach((file) => {
        formData.append('files', file)
      })
    } else {
      formData.append(key, value as string)
    }
  })

  const config = {
    method: 'POST',
    url: fullUrl,
    data: formData,
    headers: {
      'content-type': 'multipart/form-data',
      'accept-language': locale ? locale : 'en',
    },
  }

  return await clientApiAxiosFetch({ fullUrl, config })
}

export const sendVoiceMessage = async (file: Blob, gRecaptchaToken: string) => {
  const bareMimeType = toBareMimeType(file?.type)
  const fileName = audioUploadFileName(bareMimeType)

  // Report an unusable recording as a failed upload rather than throwing, so callers handle it on
  // the same path as any other rejected upload.
  if (!file || !fileName) {
    console.error('Unsupported voice recording type:', file?.type)
    return { data: null, ok: false }
  }

  // Older backends compare the label exactly, so codec parameters must not reach them.
  const upload = file.type === bareMimeType ? file : new Blob([file], { type: bareMimeType })

  const formData = new FormData()
  formData.append('file', upload, fileName)

  // Construct the URL
  const apiUrl = process.env.NEXT_PUBLIC_API_URL
  const fullUrl = `${apiUrl}/v1/chatbots/speech2text`

  const config = {
    method: 'POST',
    url: fullUrl,
    data: formData,
    headers: {
      'content-type': 'multipart/form-data',
      'recaptcha-token': gRecaptchaToken,
    },
  }
  try {
    return await clientApiAxiosFetch<{ question: string }>({ fullUrl, config })
  } catch (error) {
    console.error('Error uploading voice message:', error)
    return { data: null, ok: false }
  }
}

export const getChatbotLimitReach = async () => {
  const { data, ok } = await backendApiFetch<ILimitedProps>({
    endpoint: 'limitation/limit-usage',
    method: 'GET',
    isProtected: true,
  })

  return {
    data,
    ok,
  }
}
export const getPublicChatbotRealtimeVoiceChatLimitReach = async (chatbotId: string) => {
  const { data, ok } = await backendApiFetch<IPublicRealtimeVoiceChatLimitStatus>({
    endpoint: `limitation/real-time-voice-limit/${chatbotId}`,
    method: 'GET',
  })

  return {
    data,
    ok,
  }
}

export const publicUploadChatbotLogo = async (file: File) => {
  if (!file) {
    throw new Error('File is required')
  }
  const contentType = file.type

  // Validate file type
  if (!CHATBOT_LOGO_SUPPORTED_IMAGE_TYPES.includes(contentType)) {
    throw new Error('File is not of supported image type')
  }

  const formData = new FormData()
  formData.append('file', file)

  // Construct the URL
  const apiUrl = process.env.NEXT_PUBLIC_API_URL
  const fullUrl = `${apiUrl}/v1/chatbots/trial/logo`

  const config = {
    method: 'POST',
    url: fullUrl,
    data: formData,
    headers: {
      'content-type': 'multipart/form-data',
    },
    withCredentials: true,
  }
  try {
    const { data, ok } = await clientApiAxiosFetch({ fullUrl, config })
    return { data, ok }
  } catch (error) {
    console.error('Error uploading chatbot logo:', error)
    return { data: null, ok: false }
  }
}

export const pubicUpdateChatbotService = async (body: PublicUpdateChatbotParams) => {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL
  const fullUrl = `${apiUrl}/v1/chatbots/trial`

  const config = {
    method: 'PUT',
    url: fullUrl,
    data: body,
    withCredentials: true,
  }
  try {
    const { data, ok } = await clientApiAxiosFetch({ fullUrl, config })
    return { data, ok }
  } catch (error) {
    console.error('Error updating chatbot:', error)
    return { data: null, ok: false }
  }
}

export const publicCancelOnboarding = async () => {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL
  const fullUrl = `${apiUrl}/v1/chatbots/trial`

  const config = {
    method: 'DELETE',
    url: fullUrl,
    withCredentials: true,
  }
  try {
    const { data, ok } = await clientApiAxiosFetch({ fullUrl, config })
    return { data, ok }
  } catch (error) {
    console.error('Error canceling onboarding:', error)
    return { data: null, ok: false }
  }
}

/**
 * @deprecated use startOnboardingOrgTrialHandler
 */
export const startTrialHandler = async ({ priceId }: { priceId: string }) => {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL
  const fullUrl = `${apiUrl}/v1/chatbots/start-trial`
  const session = await getAuthSession()

  const config = {
    method: 'POST',
    url: fullUrl,
    data: { priceId },
    headers: {
      Authorization: `Bearer ${session?.jwtToken}`,
    },
    withCredentials: true,
  }
  try {
    // TODO: use backendApiFetch
    const { data, ok } = await clientApiAxiosFetch({ fullUrl, config })
    return { data, ok }
  } catch (error) {
    console.error('Error Starting Trial:', error)
    return { data: null, ok: false }
  }
}

export const updateChatbotPassword = async ({
  isPasswordProtected,
  password,
}: {
  isPasswordProtected: boolean
  password: string
}) => {
  const { data, ok } = await backendApiFetch<{ data: { isPasswordProtected: boolean } }>({
    endpoint: 'chatbots/password-protection',
    method: 'PATCH',
    body: { isPasswordProtected, password },
    isProtected: true,
  })

  return {
    data,
    ok,
  }
}

export const verifyChatbotPassword = async ({ chatbotId, password }: { chatbotId: string; password: string }) => {
  const { data, ok } = await backendApiFetch<{ data: { accessToken?: string; isValid: boolean } }>({
    endpoint: 'chatbots/' + chatbotId + '/verify-password',
    method: 'POST',
    body: { password },
  })

  return {
    data,
    ok,
  }
}

export const verifyChatbotToken = async ({ chatbotId, token }: { chatbotId: string; token: string }) => {
  const { ok, data } = await backendApiFetch<{ ok: boolean; message: string }>({
    endpoint: `chatbots/${chatbotId}/verify-token`,
    method: 'POST',
    body: { token },
  })

  return {
    ok,
    data,
  }
}

export const getChatbotPassword = async () => {
  const { data, ok } = await backendApiFetch<{ data: { password: string } }>({
    endpoint: 'chatbots/password',
    method: 'GET',
    isProtected: true,
  })

  return {
    data,
    ok,
  }
}

export const getTestChatbotEscalationVariables = async (question: string) => {
  const { data, ok } = await backendApiFetch<EscalationVariablesType>({
    endpoint: `chatbots/test/escalation-variables?question=${encodeURIComponent(question)}`,
    method: 'GET',
    isProtected: true,
  })

  return {
    data,
    ok,
  }
}

export const getEmbedChatbotEscalationVariables = async (
  chatbotId: string,
  question: string,
) => {
  return backendApiFetch<EscalationVariablesType>({
    endpoint: `chatbots/embed/escalation-variables?chatbotId=${chatbotId}&question=${encodeURIComponent(
      question,
    )}`,
    method: 'GET',
  })
}
